import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  UserKeys,
  betaGetGmailForwarding,
  betaUpdateGmailForwarding,
} from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { ForwardingModel } from '../../../../../types';
import { Controller, useForm } from 'react-hook-form';

import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import ForwardingAutocomplete from './forwarding-addresses/forwarding-autocomplete';
import { Link, useParams } from 'react-router-dom';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { Messages } from '../../../../utils/message-constants';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

export default function ForwardingPage() {
  const { userEmail } = useParams() as { userEmail: string };
  const { userRoles, changeLogRoles } = useUserSettings();
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();

  const {
    data: gmailForwarding,
    isFetching,
    isLoading,
  } = useQuery({
    queryKey: [UserKeys.GmailForwarding, userEmail],
    queryFn: () => betaGetGmailForwarding(userEmail),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('gmail forwarding'),
    },
  });
  interface FormInput {
    emailAddress: string;
    disposition: string;
    enabled: boolean;
  }

  const defaultValues = {
    emailAddress: gmailForwarding?.emailAddress
      ? gmailForwarding.emailAddress
      : '',
    disposition: gmailForwarding?.disposition
      ? gmailForwarding.disposition
      : 'archive',
    enabled: gmailForwarding?.enabled ? gmailForwarding.enabled : false,
  };

  const {
    handleSubmit,
    control,
    setValue,
    formState: { isDirty },
    watch,
    getValues,
  } = useForm<FormInput>({
    values: defaultValues,
  });
  const onSubmit = (data: FormInput) => {
    if (
      isAbleToSeeUIElement(changeLogRoles, UserRoles.EditGmailForwarding.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      if (data.enabled) {
        mutate(data);
      } else {
        const disableForwarding = {
          enabled: false,
        } as ForwardingModel;
        mutate(disableForwarding);
      }
    }
  };

  const enabledSwitch = watch('enabled');
  const emailAddressValue = watch('emailAddress');

  const { mutate, isPending: isUpdating } = useMutation({
    mutationFn: (data: ForwardingModel) =>
      betaUpdateGmailForwarding(userEmail, data, changeLogMessage),
    onSuccess: (data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Gmail forwarding'),
      });
      queryClient.setQueryData([UserKeys.GmailForwarding, userEmail], data);

      setChangeLogMessage('');
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('gmail forwarding'),
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.gmail_settings.update_forwarding,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.forwarding,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  if (isLoading || isFetching) {
    return (
      <Card
        sx={{
          height: '100%',
        }}
      >
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{ width: '100%', minHeight: '100%' }}
        >
          <CircularProgress />
        </Stack>
      </Card>
    );
  }

  const actionOptions = [
    { value: 'archive', displayValue: `Archive Gmail's copy` },
    { value: 'leaveInInbox', displayValue: `Keep Gmail's copy in the inbox` },
    { value: 'markRead', displayValue: `Mark Gmail's copy as read` },
    { value: 'trash', displayValue: "Delete Gmail's copy" },
  ];

  return (
    <Card
      component={'form'}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        action={
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={
              !isDirty ||
              (enabledSwitch && emailAddressValue.length === 0) ||
              !isAbleToSeeUIElement(
                userRoles,
                UserRoles.EditGmailForwarding.value
              )
            }
            loading={isUpdating}
            color="primary"
            type="submit"
          >
            Save
          </Button>
        }
      />
      <CardContent
        sx={{ px: 4, py: 0, position: 'relative', overflowY: 'auto' }}
      >
        <Stack gap={3}>
          <Stack flexDirection="row" alignItems="center">
            <Typography>Forwarding Enabled: </Typography>
            <Controller
              name={'enabled'}
              control={control}
              render={({ field }) => {
                return (
                  <Switch
                    onChange={() => {
                      setValue('enabled', !enabledSwitch, {
                        shouldDirty: true,
                      });
                    }}
                    checked={field.value === true}
                  />
                );
              }}
            />
          </Stack>
          <Stack flexDirection="row" alignItems="center" gap={2}>
            <Typography>Current User Forwarding: </Typography>
            <Typography>
              {gmailForwarding?.emailAddress ? (
                <Chip
                  label={gmailForwarding.emailAddress}
                  color={'primary'}
                  size="small"
                />
              ) : (
                'None currently selected'
              )}
            </Typography>
          </Stack>
          <Stack flexDirection="row" alignItems="center" gap={2}>
            <Typography>Forward to: </Typography>
            <Controller
              name={'emailAddress'}
              control={control}
              render={({ field }) => (
                <Box sx={{ width: 400 }}>
                  <ForwardingAutocomplete
                    userEmail={userEmail}
                    setAutoCompleteValue={field.onChange}
                    autocompleteValue={field.value}
                    disabled={!enabledSwitch}
                  />
                </Box>
              )}
            />
          </Stack>
          <Stack flexDirection="row" alignItems="center" gap={6}>
            <Typography>Action: </Typography>
            <Controller
              name={'disposition'}
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  disabled={!enabledSwitch}
                  {...field}
                  sx={{ width: 400 }}
                >
                  {actionOptions.map((option) => (
                    <MenuItem value={option.value} key={option.displayValue}>
                      {option.displayValue}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Stack>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              if (getValues('enabled')) {
                mutate({
                  emailAddress: getValues('emailAddress'),
                  disposition: getValues('disposition'),
                  enabled: getValues('enabled'),
                });
              } else {
                mutate({
                  enabled: false,
                } as ForwardingModel);
              }
            }}
            mutateLoading={isUpdating}
          />
        </Stack>
        <Alert severity="info" sx={{ mt: 4, width: 'fit-content' }}>
          To forward to an external email address, it must first be added{' '}
          <Link to="../forwarding-addresses">here</Link> and then accepted by
          the user.
        </Alert>
      </CardContent>
    </Card>
  );
}
